import cn from 'classnames';
import {
  State,
  useSelector,
  useHistory,
  useDispatch,
  Dispatch,
} from '@medlinker/fundamental';
import React, { ReactNode, useCallback, useEffect, useState } from 'react';

import useDidMount from '../../hooks/useDidMount';
import LocationModal from './LocationModal';
import env from '../../utils/env';
import { getAreaByPos, getUserLastArea, setUserHistoryArea } from './api';
import styles from './index.module.scss';

interface Props {
  wxConfig: any;
  className?: string;
  left?: ReactNode;
  right?: ReactNode;
}

export const LocationWithCith = ({
  wxConfig,
  className,
  left,
  right,
}: Props) => {
  const dispatch: Dispatch = useDispatch();

  const { currCity, homePosition } = useSelector((state: State) => {
    return state.location;
  });

  const [modalInfo, setModalInfo] = useState<any>({
    visible: false,
    onOk: () => {},
    onCancel: () => {},
    title: '提示',
    tips: '',
    cancelText: '取消',
    okText: '确定',
  });

  const onGetLastLocation = async () => {
    const res = await getUserLastArea()
      .then(v => {
        return v;
      })
      .catch(() => {
        return null;
      });

    if (res && res.cityName) {
      dispatch.location.setCity(res.cityName);
    } else {
      // 请求错误（或者没有网络），默认展示北京市
      dispatch.location.setCity('北京市');
    }
  };

  // 获取微信定位城市
  const onGetWxLocation = useCallback(() => {
    // 只处理每次打开网址进入首页的微信定，网址内路由切换之后不重新处理
    if (env.isWechat && homePosition) {
      wxConfig.ready(() => {
        dispatch.location.setHomePosition(false);
        wx.getLocation({
          type: 'wgs84', // 默认为wgs84的gps坐标，如果要返回直接给openLocation用的火星坐标，可传入'gcj02'
          success: async (res: any) => {
            console.log('======getLocation success======');
            console.log(res);
            // 1、微信定位成功，得到经纬度
            const latitude = res.latitude; // 纬度，浮点数，范围为90 ~ -90
            const longitude = res.longitude; // 经度，浮点数，范围为180 ~ -180。
            // 2、根据经纬度获取当前位置
            const pos = await getAreaByPos({
              isArea: false,
              lat: latitude,
              lng: longitude,
            });

            console.log(pos);
            // 3、获取到城市，判断当前城市是否一致
            // 定位成功，而且和当前位置不相同，而且还是登录过的才能提示切换
            // 首页已经是要求必须登录的，所以这里不用
            if (pos && pos.city && pos.city !== currCity) {
              // 3、获取到城市，判断城市是否一致，一致就不弹窗
              modalInfo.visible = true;
              modalInfo.title = '提示';
              modalInfo.tips = `您当前的位置在${pos.city}，是否切换到该城市？`;
              modalInfo.okText = '切换';
              modalInfo.cancelText = '取消';
              modalInfo.cancelProps = {
                display: 'block',
              };
              modalInfo.onCancel = () => {
                modalInfo.visible = false;
                setModalInfo({ ...modalInfo });
              };
              modalInfo.onOk = async () => {
                // 设置位置
                await setUserHistoryArea({
                  role: 2,
                  cityId: pos.cityId,
                });
                // 更新当前位置
                dispatch.location.setCity(pos.city);
                setTimeout(() => {
                  modalInfo.visible = false;
                  setModalInfo({ ...modalInfo });
                }, 100);
              };
              setModalInfo({ ...modalInfo });
            }
          },
          fail: (err: any) => {
            console.log('======getLocation fail======');
            console.log(err);
            // 没有权限，进行弹窗
            const notPermission =
              err && JSON.stringify(err).indexOf('permission') !== -1;

            if (notPermission) {
              // 没有开启小程序权限，弹窗提示
              modalInfo.visible = true;
              modalInfo.title = '温馨提示';
              modalInfo.tips =
                '获取位置失败，为您展示默认信息；为了获得更好的服务，请打开【位置信息】';
              modalInfo.okText = '确定';
              modalInfo.cancelProps = {
                display: 'none',
              };
              modalInfo.onOk = () => {
                modalInfo.visible = false;
                setModalInfo({ ...modalInfo });
              };
              setModalInfo({ ...modalInfo });
            }
          },
        });
      });
    }
  }, [dispatch, modalInfo, currCity, homePosition, wxConfig]);

  useEffect(() => {
    if (currCity) {
      setTimeout(() => {
        onGetWxLocation();
      }, 500);
    }

    return () => {};
  }, [currCity, onGetWxLocation]);

  useDidMount(() => {
    // 上次定位接口调用
    onGetLastLocation();

    // 如果是从医生列表返回来的，需要清楚医生列表的state
    if (history.action === 'POP') {
      // @ts-ignore
      if (dispatch.doctorListCombined) {
        // @ts-ignore
        dispatch.doctorListCombined.resetState();
      }
    }
  });

  const history = useHistory();
  const onGotoLocation = () => {
    history.push(`/tools/location-switch`);
  };

  return (
    <>
      <div
        className={cn(styles.cityInfo, className)}
        onClick={() => onGotoLocation()}
      >
        {left}
        <span>{currCity}</span>
        {right || <img src={require('./images/arrow.png')} alt="logo" />}
      </div>
      <LocationModal {...modalInfo} />
    </>
  );
};

export { default as Location } from './Location';
export * from './types';
